











import { Mixins } from 'vue-mixin-decorator'
import { Component, Prop } from 'vue-property-decorator'

import { log } from '@/log'
import { PaymentType } from '@/models/app-config/behavior/types'
import { CookieService } from '@/services/cookie'
import ExternalPaymentCallbackView from '@ajax/vue/components/organisms/external-payment/ExternalPaymentCallbackView.vue'
import { HlAppViewModelBuilder } from '@ajax/vue/components/organisms/external-payment/model/hl-app-view-model'
import { createExternalPaymentInteraction, ExternalPaymentInteraction, ValidateApiResponse } from '@ajax/vue/components/organisms/external-payment/model/interaction'
import { DefaultViewModelBuilder, ViewModel } from '@ajax/vue/components/organisms/external-payment/model/view-model'
import { HlAppModeMixin } from '@spa/mixins/hl-app-mode'

@Component({
  components: {
    ExternalPaymentCallbackView,
  },
})
export default class ExternalPaymentCallback extends Mixins<HlAppModeMixin>(HlAppModeMixin) {
  @Prop({ required: true })
  paymentType: PaymentType

  @Prop({ required: true })
  status: 'success' | 'cancel' | 'error'

  @Prop({ required: true })
  encodedData: string

  @Prop({ required: true })
  apiPathBase: string

  @Prop({ required: true })
  encodedQueries: string

  viewModel: ViewModel | null = null

  // 形式の詳細は知る必要がないのでany
  get postData(): any {
    return JSON.parse(this.encodedData)
  }

  get interaction(): ExternalPaymentInteraction {
    return createExternalPaymentInteraction({
      forHlApp: this.forHlApp,
    })
  }

  async created() {
    switch (this.status) {
      case 'success':
        return await this.setupViewModelForSuccess()
      case 'error':
        return await this.setupViewModelForError()
      case 'cancel':
        return await this.setupViewModelForCancel()
      default:
        log.error(`Unexpected status ${this.status}`)
        this.viewModel = this.fallbackViewModel
    }
  }

  async setupViewModelForSuccess() {
    if (!this.userIsRegistered) {
      // 決済は成功したと思われるがログインしていないブラウザで表示されたケース
      log.debug('Need login to show order complete page.')

      this.viewModel = this.forHlApp
        ? HlAppViewModelBuilder.forPaymentSuccessfullyDone()
          .addCheckOrderHistoryErrorMessage()
          .withCheckOrderHistoryAlertSection()
          .buildWithHlAppOrderHistory()
        : DefaultViewModelBuilder.forPaymentSuccessfullyDone()
          .addCheckOrderHistoryErrorMessage()
          .withCheckOrderHistoryAlertSection()
          .buildWithOrderHistoryViaLoginLink()

      return
    }

    (await this.interaction.callValidateApi(this.apiPathBase, this.encodedQueries, this.postData)).fold(
      (l: Error) => {
        log.error({ error: l })
        this.viewModel = this.fallbackViewModel
      },
      (r: ValidateApiResponse) => {
        this.viewModel = this.interaction.convertValidateApiResponseToViewModel(r)
      }
    )
  }

  async setupViewModelForError() {
    if (this.forHlApp) {
      this.viewModel = HlAppViewModelBuilder.forPaymentError()
        .addGuideToReorderErrorMessage()
        .withReorderAlertSection()
        .buildWithHlAppReorder()

      return
    }

    const builder = DefaultViewModelBuilder.forPaymentError()
      .addGuideToReorderErrorMessage()
      .withReorderAlertSection()
    this.viewModel = this.userIsRegistered
      ? builder.buildWithReorderLink()
      : builder.buildWithReorderViaLoginLink()
  }

  async setupViewModelForCancel() {
    if (this.forHlApp) {
      this.viewModel = HlAppViewModelBuilder.forPaymentCanceled()
        .withReorderAlertSectionOnCancel()
        .buildWithHlAppReorder()

      return
    }
    const builder = DefaultViewModelBuilder.forPaymentCanceled()
      .withReorderAlertSectionOnCancel()

    this.viewModel = this.userIsRegistered
      ? builder.buildWithReorderLink()
      : builder.buildWithReorderViaLoginLink()
  }

  private get userIsRegistered(): boolean {
    return CookieService.isRegistered()
  }

  /**
   * ホテラバアプリでの注文によるコールバック画面表示であるかどうか
   *
   * 本当はホテラバアプリの注文かどうかでコールバックのURLを切り替えるくらいのことを検討しても良いが、
   * 工数の都合でどちらも同じコールバックURLに返ってくる。
   * 一方で注文完了画面への遷移など、その後の挙動を切り替える必要があるため、このページで分岐させる。
   *
   * 今のところはホテラバアプリのUAであるかどうかを基準とするが、内部的な条件は露出させず、
   * このページが「ホテラバアプリ用」であるべきかどうかをbooleanで提供する。
   */
  private get forHlApp(): boolean {
    return this.hlAppMode.isViewedInHlAppWebView
  }

  /**
   * 予期しないエラーが起こった際に表示する内容
   */
  private get fallbackViewModel(): ViewModel {
    return this.forHlApp
      ? HlAppViewModelBuilder.forUnexpectedError()
        .addMaybeErrorErrorMessage()
        .buildWithHlAppOrderHistory()
      : DefaultViewModelBuilder.forUnexpectedError()
        .addMaybeErrorErrorMessage()
        .buildWithOrderHistoryLink()
  }
}
